import React from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatGamingMoney } from '../../utils/gamingCurrency'
import { formatSydneyTime } from '../../utils/sydneyDateTime'
import { buildGamingDataUrl, alertWindowBounds } from './alertContextUtils'

const FILTERS = [
  { id: 'walks', label: 'All walks' },
  { id: 'under_15', label: 'Under 15 min' },
  { id: 'egm_egm', label: 'EGM to EGM' },
  { id: 'egm_terminal', label: 'EGM to terminal' },
  { id: 'open', label: 'Still open' },
  { id: 'same', label: 'Same machine' },
  { id: 'earlier', label: 'Printed earlier' },
]

function matchesFilter(walk, filter) {
  if (filter === 'walks') return walk.kind === 'egm_egm' || walk.kind === 'egm_terminal' || walk.kind === 'other'
  if (filter === 'under_15') {
    return (walk.kind === 'egm_egm' || walk.kind === 'egm_terminal' || walk.kind === 'other')
      && walk.minutes != null && walk.minutes < 15
  }
  return walk.kind === filter
}

const TRACE_FILTERS = [
  { id: 'multi', label: '2 or more tickets' },
  { id: 'all', label: 'All walks' },
  { id: 'cash_in', label: 'Started with cash in' },
  { id: 'earlier', label: 'Started with an earlier ticket' },
  { id: 'terminal', label: 'Ended at a terminal' },
]

function matchesTrace(trace, filter) {
  if (filter === 'multi') return trace.tickets >= 2
  if (filter === 'cash_in') return trace.start === 'cash_in'
  if (filter === 'earlier') return trace.start === 'earlier'
  if (filter === 'terminal') return trace.ended === 'terminal'
  return true
}

function stationsFromTrace(trace) {
  const stations = []
  const absorb = (stop) => {
    const prev = stations[stations.length - 1]
    if (prev && prev.place === stop.place) {
      if (stop.inTime) prev.inTime = stop.inTime
      if (stop.outTime) prev.outTime = stop.outTime
      if (stop.ticketOut) prev.ticketOut = stop.ticketOut
      if (stop.href) prev.href = stop.href
      if (stop.amount != null) prev.amount = stop.amount
      if (stop.end) prev.end = true
      if (stop.terminal) prev.terminal = true
      if (stop.open) prev.open = true
      if (stop.role === 'Terminal' || stop.role === 'Still open') prev.role = stop.role
      return prev
    }
    stations.push(stop)
    return stop
  }

  ;(trace.steps || []).forEach((step, i) => {
    const origin = absorb({
      key: `from-${step.ticket_id}-${i}`,
      place: step.from_place || '—',
      role: i === 0 ? (step.arrived_by === 'earlier' ? 'Earlier ticket' : 'Cash in') : 'EGM',
      outTime: step.from_time,
      ticketOut: step.ticket_id,
      amount: step.amount,
      href: sitHref(step.from_place, step.from_time),
    })
    if (!origin.outTime && !origin.inTime && step.to_time) origin.fileTime = step.to_time
    origin.depart = {
      amount: step.amount,
      minutes: step.minutes,
      ticket: step.ticket_id,
      time: step.from_time || step.to_time,
    }
    if (step.to_place) {
      absorb({
        key: `to-${step.ticket_id}-${i}`,
        place: step.to_place,
        role: step.to_kind === 'terminal' ? 'Terminal' : 'EGM',
        inTime: step.to_time,
        amount: step.amount,
        ticketOut: step.ticket_id,
        href: step.to_kind === 'egm' ? sitHref(step.to_place, step.to_time) : null,
        end: i === trace.steps.length - 1,
        terminal: step.to_kind === 'terminal',
      })
    } else {
      origin.open = true
      origin.end = true
      origin.role = i === 0 ? origin.role : 'Still open'
    }
  })
  return stations
}

function signedMoney(value) {
  const n = Math.round(Number(value) * 100) / 100
  if (!Number.isFinite(n)) return ''
  return formatGamingMoney(n)
}

function moneyTone(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || Math.abs(n) < 0.005) return 'text-slate-500'
  return n > 0 ? 'text-emerald-700' : 'text-red-700'
}

const FLOW_W = 176
const FLOW_H = 78
const FLOW_GAP_X = 40
const FLOW_GAP_Y = 14

function clockRange(from, to) {
  const a = from ? formatSydneyTime(from) : ''
  const b = to ? formatSydneyTime(to) : ''
  if (a && b && a !== b) return `${a}–${b}`
  return b || a || ''
}

function flowTree(trace) {
  const steps = trace.steps || []
  const byId = new Map(steps.map((step) => [String(step.ticket_id), step]))
  const rootStep = byId.get(String(trace.ticket_id)) || steps[steps.length - 1]
  if (!rootStep) return null

  function make(step, seen) {
    if (!step || seen.has(String(step.ticket_id))) return null
    const nextSeen = new Set(seen)
    nextSeen.add(String(step.ticket_id))
    const children = (step.from_tickets || [])
      .map((ticket) => make(byId.get(String(ticket.ticket_id)), nextSeen))
      .filter(Boolean)
    return { step, children }
  }

  const root = make(rootStep, new Set())
  if (!root) return null

  const setCol = (node) => {
    if (!node.children.length) {
      node.col = 0
      return
    }
    node.children.forEach(setCol)
    node.col = Math.max(...node.children.map((child) => child.col)) + 1
  }
  setCol(root)

  let cursor = 0
  const setRow = (node) => {
    if (!node.children.length) {
      node.row = cursor
      cursor += 1
      return
    }
    node.children.forEach(setRow)
    const rows = node.children.map((child) => child.row)
    node.row = (Math.min(...rows) + Math.max(...rows)) / 2
  }
  setRow(root)

  const nodes = []
  const collect = (node) => {
    nodes.push(node)
    node.children.forEach(collect)
  }
  collect(root)

  let end = null
  if (root.step.to_kind === 'terminal' && root.step.to_place) {
    end = {
      place: root.step.to_place,
      time: root.step.to_time,
      amount: root.step.amount,
      col: root.col + 1,
      row: root.row,
    }
  }

  const maxCol = end ? end.col : root.col
  const maxRow = Math.max(...nodes.map((node) => node.row), end ? end.row : 0)
  return { root, nodes, end, maxCol, maxRow }
}

function flowBox(node) {
  const step = node.step
  const kids = node.children
  const ticket = step.ticket_id ? String(step.ticket_id) : ''
  if (!kids.length) {
    const prior = step.arrived_by === 'earlier'
    const when = step.from_time || step.to_time
    return {
      tone: 'border-slate-300 bg-slate-50',
      kicker: prior ? 'Prior ticket' : 'Cash in',
      place: step.from_place || '—',
      when: when ? formatSydneyTime(when) : '',
      lines: [formatGamingMoney(step.amount)],
      ticket,
      href: sitHref(step.from_place, when),
    }
  }
  const arrived = kids.reduce((sum, child) => sum + (Number(child.step.amount) || 0), 0)
  const change = (Number(step.amount) || 0) - arrived
  const arrivedAt = kids.map((child) => child.step.to_time).filter(Boolean).sort()
  const when = clockRange(arrivedAt[0], step.from_time)
  if (kids.length === 1) {
    return {
      tone: 'border-teal-500 bg-teal-50',
      kicker: '',
      place: step.from_place || '—',
      when,
      lines: [{ text: `${signedMoney(change)} → ${formatGamingMoney(step.amount)}`, tone: moneyTone(change) }],
      ticket,
      href: sitHref(step.from_place, step.from_time),
    }
  }
  return {
    tone: 'border-rose-400 bg-rose-50',
    kicker: '',
    place: step.from_place || '—',
    when,
    lines: [
      `${kids.length} tickets in · ${formatGamingMoney(arrived)}`,
      { text: `out ${formatGamingMoney(step.amount)} (${signedMoney(change)})`, tone: moneyTone(change) },
    ],
    ticket,
    href: sitHref(step.from_place, step.from_time),
  }
}

function flowNote(nodes, end) {
  const leaves = nodes.filter((node) => !node.children.length)
  const earlier = leaves.filter((node) => node.step.arrived_by === 'earlier')
  const cash = leaves.filter((node) => node.step.arrived_by !== 'earlier')
  const earlierSum = earlier.reduce((sum, node) => sum + (Number(node.step.amount) || 0), 0)
  const parts = []
  if (earlier.length) {
    parts.push(`${formatGamingMoney(earlierSum)} prior ticket${earlier.length === 1 ? '' : 's'}`)
  }
  if (cash.length) {
    parts.push(`${cash.length} cash-in${cash.length === 1 ? '' : 's'} (amounts on meters)`)
  }
  const out = end?.amount ?? nodes.find((node) => node.col === Math.max(...nodes.map((item) => item.col)))?.step.amount
  return `Known tickets in: ${parts.join(' + ') || '—'} · Out: ${formatGamingMoney(out)}`
}

function boxPosition(col, row) {
  return {
    left: col * (FLOW_W + FLOW_GAP_X),
    top: row * (FLOW_H + FLOW_GAP_Y),
  }
}

function WalkFlow({ trace }) {
  const tree = flowTree(trace)
  if (!tree) return null
  const { nodes, end, maxCol, maxRow } = tree
  const width = (maxCol + 1) * (FLOW_W + FLOW_GAP_X) - FLOW_GAP_X
  const height = (Math.floor(maxRow) + 1) * (FLOW_H + FLOW_GAP_Y) - FLOW_GAP_Y
  const edges = []
  nodes.forEach((node) => {
    node.children.forEach((child) => edges.push([child, node]))
  })
  if (end) edges.push([tree.root, end])

  const point = (item, side) => {
    const pos = boxPosition(item.col, item.row)
    return {
      x: pos.left + (side === 'right' ? FLOW_W : 0),
      y: pos.top + FLOW_H / 2,
    }
  }

  const labels = []
  if (nodes.some((node) => node.children.length > 1)) {
    labels.push([0, 'start'])
    const stacked = nodes.find((node) => node.children.length > 1)
    if (stacked) labels.push([stacked.col, 'stacked sit'])
    nodes.forEach((node) => {
      if (node.children.length === 1 && !labels.some((label) => label[0] === node.col)) {
        labels.push([node.col, 'sit'])
      }
    })
    if (end) labels.push([end.col, 'end'])
  }

  return (
    <div>
      <div className="relative" style={{ width, height: height + (labels.length ? 18 : 0), marginTop: labels.length ? 18 : 0 }}>
        {labels.map(([col, text]) => (
          <div
            key={text + col}
            className="absolute text-[10px] uppercase tracking-wide text-slate-400"
            style={{ left: col * (FLOW_W + FLOW_GAP_X), top: -16, width: FLOW_W }}
          >
            {text}
          </div>
        ))}
        <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
          {edges.map(([from, to], index) => {
            const a = point(from, 'right')
            const b = point(to, 'left')
            const mid = (a.x + b.x) / 2
            return (
              <path
                key={index}
                d={`M ${a.x} ${a.y + (labels.length ? 0 : 0)} C ${mid} ${a.y}, ${mid} ${b.y}, ${b.x} ${b.y}`}
                fill="none"
                stroke="#94a3b8"
                strokeWidth="1.25"
              />
            )
          })}
        </svg>
        {nodes.map((node) => {
          const box = flowBox(node)
          const pos = boxPosition(node.col, node.row)
          return (
            <div
              key={node.step.ticket_id}
              className={`absolute flex flex-col justify-center rounded border px-2 py-1 text-xs leading-4 ${box.tone}`}
              style={{ left: pos.left, top: pos.top, width: FLOW_W, height: FLOW_H }}
            >
              {box.kicker ? <div className="text-[10px] uppercase tracking-wide text-slate-500">{box.kicker}</div> : null}
              <div className="font-semibold text-slate-900">
                {/^\d{6}$/.test(box.place || '') ? <span className="mr-1 text-[10px] uppercase tracking-wide text-slate-500">EGM</span> : null}
                {box.href ? (
                  <Link className="font-mono hover:text-psg-teal hover:underline" to={box.href}>{box.place}</Link>
                ) : (
                  <span className="font-mono">{box.place}</span>
                )}
                {box.when ? <span className="ml-1 font-normal text-slate-600">{box.when}</span> : null}
              </div>
              {box.lines.map((line) => {
                const text = typeof line === 'string' ? line : line.text
                const tone = typeof line === 'string' ? 'text-slate-700' : (line.tone || 'text-slate-700')
                return <div key={text} className={tone}>{text}</div>
              })}
              {box.ticket ? <div className="truncate font-mono text-[10px] text-slate-400">{box.ticket}</div> : null}
            </div>
          )
        })}
        {end && (
          <div
            className="absolute flex flex-col justify-center rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs leading-4"
            style={{ ...boxPosition(end.col, end.row), width: FLOW_W, height: FLOW_H }}
          >
            <div className="font-semibold text-slate-900">Terminal {end.place}</div>
            <div className="text-slate-700">
              {end.time ? formatSydneyTime(end.time) : ''} · {formatGamingMoney(end.amount)}
            </div>
          </div>
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">{flowNote(nodes, end)}</p>
    </div>
  )
}

function traceHeadline(trace) {
  const stations = stationsFromTrace(trace)
  const first = stations[0] || null
  const last = stations.length > 1 ? stations[stations.length - 1] : null
  const startAmount = first?.amount ?? null
  const endAmount = last?.amount ?? null
  const cumulative = startAmount != null && endAmount != null ? endAmount - startAmount : null
  return {
    first,
    last,
    startAmount,
    endAmount,
    cumulative,
    tickets: Number(trace.tickets) || stations.length || 0,
    terminal: Boolean(last?.terminal),
  }
}

function passesWalkFilters(trace, { ticketMin, amountMin, result }) {
  const row = traceHeadline(trace)
  const tickets = Number(ticketMin)
  if (ticketMin !== '' && Number.isFinite(tickets) && row.tickets < tickets) return false
  const amount = Number(amountMin)
  const compared = row.endAmount != null ? row.endAmount : row.startAmount
  if (amountMin !== '' && Number.isFinite(amount) && (compared == null || compared < amount)) return false
  if (result === 'gain' && !(row.cumulative > 0.005)) return false
  if (result === 'loss' && !(row.cumulative < -0.005)) return false
  return true
}

function traceTicketIds(trace) {
  const ids = new Set()
  if (trace?.ticket_id) ids.add(String(trace.ticket_id))
  for (const step of trace?.steps || []) {
    if (step?.ticket_id) ids.add(String(step.ticket_id))
  }
  return [...ids]
}

function tracePlaces(trace) {
  const places = []
  for (const step of trace?.steps || []) {
    if (step?.from_place) places.push(String(step.from_place))
    if (step?.to_place) places.push(String(step.to_place))
  }
  return places
}

function traceTimes(trace) {
  const times = []
  for (const step of trace?.steps || []) {
    for (const raw of [step?.from_time, step?.to_time]) {
      const n = raw ? new Date(raw).getTime() : NaN
      if (Number.isFinite(n)) times.push(n)
    }
  }
  return times
}

function passesJourneySearch(trace, search) {
  const ticketQ = String(search.ticket || '').trim().toLowerCase()
  if (ticketQ && !traceTicketIds(trace).some((id) => id.toLowerCase().includes(ticketQ))) return false
  const egmQ = String(search.egm || '').trim().toLowerCase()
  if (egmQ && !tracePlaces(trace).some((place) => place.toLowerCase().includes(egmQ))) return false
  const times = traceTimes(trace)
  if (search.timeFrom) {
    const from = new Date(search.timeFrom).getTime()
    if (Number.isFinite(from) && times.length && Math.max(...times) < from) return false
  }
  if (search.timeTo) {
    const to = new Date(search.timeTo).getTime()
    if (Number.isFinite(to) && times.length && Math.min(...times) > to) return false
  }
  const row = traceHeadline(trace)
  const compared = row.endAmount != null ? row.endAmount : row.startAmount
  if (search.amountMax !== '' && search.amountMax != null) {
    const max = Number(search.amountMax)
    if (Number.isFinite(max) && compared != null && compared > max) return false
  }
  return true
}

function placesMatch(a, b) {
  const left = String(a || '').trim()
  const right = String(b || '').trim()
  if (!left || !right) return false
  return left === right || left.endsWith(right) || right.endsWith(left)
}

function traceTouchesAlert(trace, anchor) {
  if (!anchor) return false
  const places = tracePlaces(trace)
  if (anchor.egm && !places.some((place) => placesMatch(place, anchor.egm))) return false
  if (!anchor.at) return Boolean(anchor.egm)
  const at = new Date(anchor.at).getTime()
  if (!Number.isFinite(at)) return Boolean(anchor.egm)
  const times = traceTimes(trace)
  if (!times.length) return Boolean(anchor.egm)
  const pad = 6 * 60 * 60 * 1000
  return times.some((time) => Math.abs(time - at) <= pad)
}

function hopMatchesSearch(walk, search) {
  const ticketQ = String(search.ticket || '').trim().toLowerCase()
  if (ticketQ && !String(walk?.ticket_id || '').toLowerCase().includes(ticketQ)) return false
  const egmQ = String(search.egm || '').trim().toLowerCase()
  if (egmQ) {
    const places = [walk?.from_place, walk?.to_place].filter(Boolean).map(String)
    if (!places.some((place) => place.toLowerCase().includes(egmQ))) return false
  }
  const times = [walk?.from_time, walk?.to_time]
    .map((raw) => (raw ? new Date(raw).getTime() : NaN))
    .filter((n) => Number.isFinite(n))
  if (search.timeFrom) {
    const from = new Date(search.timeFrom).getTime()
    if (Number.isFinite(from) && times.length && Math.max(...times) < from) return false
  }
  if (search.timeTo) {
    const to = new Date(search.timeTo).getTime()
    if (Number.isFinite(to) && times.length && Math.min(...times) > to) return false
  }
  if (search.amountMax !== '' && search.amountMax != null) {
    const max = Number(search.amountMax)
    const amount = Number(walk?.amount)
    if (Number.isFinite(max) && Number.isFinite(amount) && amount > max) return false
  }
  return true
}

function walkTouchesAlert(walk, anchor) {
  if (!anchor) return false
  const places = [walk?.from_place, walk?.to_place].filter(Boolean)
  if (anchor.egm && !places.some((place) => placesMatch(place, anchor.egm))) return false
  if (!anchor.at) return Boolean(anchor.egm)
  const at = new Date(anchor.at).getTime()
  if (!Number.isFinite(at)) return Boolean(anchor.egm)
  const times = [walk?.from_time, walk?.to_time]
    .map((raw) => (raw ? new Date(raw).getTime() : NaN))
    .filter((n) => Number.isFinite(n))
  if (!times.length) return Boolean(anchor.egm)
  const pad = 6 * 60 * 60 * 1000
  return times.some((time) => Math.abs(time - at) <= pad)
}

function TraceRow({ trace, highlighted, selectedIds, onToggleTicket }) {
  const [open, setOpen] = React.useState(highlighted)
  const row = traceHeadline(trace)
  if (!row.first) return null
  const cell = 'px-2 py-1.5 align-middle'
  const ids = traceTicketIds(trace)
  const allOn = ids.length > 0 && ids.every((id) => selectedIds.has(id))
  return (
    <>
      <tr
        className={`cursor-pointer border-t border-slate-200 hover:bg-slate-50 ${highlighted ? 'bg-amber-50' : ''}`}
        onClick={() => setOpen((value) => !value)}
      >
        <td className={`${cell} w-10 text-slate-400`} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="mr-1 align-middle"
            checked={allOn}
            disabled={!ids.length}
            title={allOn ? 'Leave these tickets off the investigation' : 'Include these tickets'}
            onChange={() => onToggleTicket(ids, !allOn)}
          />
          {open ? '▾' : '▸'}
        </td>
        <td className={`${cell} text-right font-semibold tabular-nums text-slate-900`}>{formatGamingMoney(row.startAmount)}</td>
        <td className={`${cell} font-mono font-semibold text-slate-900`}>{row.first.place}</td>
        <td className={`${cell} text-right font-semibold tabular-nums text-slate-900`}>
          {row.last ? formatGamingMoney(row.endAmount) : '—'}
        </td>
        <td className={`${cell} font-mono font-semibold text-slate-900`}>
          {row.last ? row.last.place : '—'}
          {row.terminal ? <span className="ml-2 text-[10px] font-sans uppercase tracking-wide text-amber-700">Terminal</span> : null}
          {highlighted ? <span className="ml-2 text-[10px] font-sans uppercase tracking-wide text-amber-800">Alert</span> : null}
        </td>
        <td className={`${cell} text-right font-semibold tabular-nums ${moneyTone(row.cumulative)}`}>
          {row.cumulative == null ? '—' : signedMoney(row.cumulative)}
        </td>
        <td className={`${cell} text-right tabular-nums text-slate-700`}>{row.tickets}</td>
      </tr>
      {open && (
        <tr className="border-t border-slate-100 bg-white">
          <td colSpan={7} className="overflow-x-auto px-3 py-2">
            <WalkFlow trace={trace} />
          </td>
        </tr>
      )}
    </>
  )
}

function kindLabel(kind) {
  if (kind === 'egm_egm') return 'EGM to EGM'
  if (kind === 'egm_terminal') return 'EGM to terminal'
  if (kind === 'open') return 'Still open'
  if (kind === 'same') return 'Same machine'
  if (kind === 'earlier') return 'Printed earlier'
  return 'Other'
}

function kindTone(kind) {
  if (kind === 'egm_egm') return 'border-teal-500 bg-teal-50/80'
  if (kind === 'egm_terminal') return 'border-amber-500 bg-amber-50/80'
  if (kind === 'open') return 'border-slate-400 bg-slate-50'
  return 'border-slate-300 bg-white'
}

function sitHref(place, time) {
  if (!place || !time || !/^\d{6}$/.test(place)) return null
  const window = alertWindowBounds(time, { halfSpanMs: 30 * 60 * 1000 })
  if (!window) return null
  return buildGamingDataUrl({
    tab: 'combined',
    egm_pk: place,
    from: window.from,
    to: window.to,
    view: 'sessions',
  })
}

function Place({ code, time, role }) {
  return (
    <div className="min-w-[7.5rem]">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{role}</div>
      <div className="font-mono text-sm font-semibold text-slate-900">{code || '—'}</div>
      <div className="text-xs text-slate-500">{time ? formatSydneyTime(time) : 'Time not in this file'}</div>
    </div>
  )
}

function WalkRow({ walk, open, onToggle, highlighted, selected, onToggleTicket }) {
  const printHref = sitHref(walk.from_place, walk.from_time)
  const redeemHref = walk.to_kind === 'egm' ? sitHref(walk.to_place, walk.to_time) : null
  return (
    <div className={`rounded-lg border border-slate-200 border-l-4 ${highlighted ? 'ring-1 ring-amber-300' : ''} ${kindTone(walk.kind)}`}>
      <div className="flex items-start gap-2 px-3 pt-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={selected}
          title={selected ? 'Leave this ticket off the investigation' : 'Include this ticket'}
          onChange={() => onToggleTicket(walk.ticket_id, !selected)}
        />
        {highlighted ? <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Alert</span> : null}
      </div>
      <button type="button" className="w-full px-3 py-2 text-left" onClick={onToggle}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Place code={walk.from_place} time={walk.from_time} role="Printed" />
          <div className="flex flex-1 flex-col items-center px-2">
            <div className="relative h-px w-full bg-slate-300">
              <span className="absolute -right-1 -top-2 text-slate-400">→</span>
            </div>
            <div className="mt-1 text-xs font-medium text-slate-800">
              {formatGamingMoney(walk.amount)}
              {walk.minutes != null ? ` · ${Number(walk.minutes).toFixed(1)} min` : ''}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{kindLabel(walk.kind)}</div>
          </div>
          <Place
            code={walk.to_place}
            time={walk.to_time}
            role={walk.to_kind === 'terminal' ? 'Terminal' : walk.kind === 'open' ? 'Not redeemed' : 'Redeemed'}
          />
        </div>
      </button>
      {open && (
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200/80 px-3 py-2 text-xs text-slate-600">
          <span className="font-mono">Ticket {walk.ticket_id}</span>
          {printHref && (
            <Link className="text-psg-teal hover:underline" to={printHref}>
              Combined sit at {walk.from_place}
            </Link>
          )}
          {redeemHref && (
            <Link className="text-psg-teal hover:underline" to={redeemHref}>
              Combined sit at {walk.to_place}
            </Link>
          )}
          {walk.to_kind === 'terminal' && (
            <span>Redeemed at terminal {walk.to_place}, not on an EGM.</span>
          )}
        </div>
      )}
    </div>
  )
}

export function TitoUploadControl({ onImported }) {
  const [busy, setBusy] = React.useState(false)
  const [note, setNote] = React.useState('')

  const upload = async (file) => {
    if (!file) return
    setBusy(true)
    setNote('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(getLocalApiUrl('/api/gaming/tito/upload'), { method: 'POST', body: fd })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || j.success === false) throw new Error(j.error || 'Upload failed')
      const days = (j.trading_days || []).join(', ')
      const msg = `${j.imported ?? 0} rows imported${days ? ` · ${days}` : ''}`
      setNote(msg)
      toast.success(msg)
      if (onImported) onImported(j)
    } catch (e) {
      toast.error(e.message || 'Upload failed')
      setNote(e.message || 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="file"
        id="tito-upload"
        className="hidden"
        accept=".csv,text/csv"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) upload(f)
          e.target.value = ''
        }}
      />
      <label htmlFor="tito-upload" className="btn-primary cursor-pointer">
        {busy ? 'Importing…' : 'Upload TITO CSV'}
      </label>
      {note && <span className="text-sm text-gray-600">{note}</span>}
    </div>
  )
}

export default function TicketWalksTab({
  alertId = '',
  initialEgm = '',
  initialFrom = '',
  initialTo = '',
  initialTradingDay = '',
} = {}) {
  const [tradingDay, setTradingDay] = React.useState(initialTradingDay || '')
  const [days, setDays] = React.useState([])
  const [summary, setSummary] = React.useState(null)
  const [walks, setWalks] = React.useState([])
  const [traces, setTraces] = React.useState([])
  const [view, setView] = React.useState('walks')
  const [filter, setFilter] = React.useState('multi')
  const [ticketMin, setTicketMin] = React.useState('')
  const [amountMin, setAmountMin] = React.useState('')
  const [result, setResult] = React.useState('all')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [openKey, setOpenKey] = React.useState('')
  const [egmQuery, setEgmQuery] = React.useState(initialEgm || '')
  const [ticketQuery, setTicketQuery] = React.useState('')
  const [timeFrom, setTimeFrom] = React.useState(initialFrom || '')
  const [timeTo, setTimeTo] = React.useState(initialTo || '')
  const [amountMax, setAmountMax] = React.useState('')
  const [alertAnchor, setAlertAnchor] = React.useState(null)
  const [selectedIds, setSelectedIds] = React.useState(() => new Set())
  const [caseNumber, setCaseNumber] = React.useState('')
  const [savingCase, setSavingCase] = React.useState(false)

  const load = React.useCallback(async (day) => {
    setLoading(true)
    setError('')
    try {
      const q = day ? `?trading_day=${encodeURIComponent(day)}` : ''
      const res = await authFetch(getLocalApiUrl(`/api/gaming/tito/walks${q}`))
      const j = await res.json().catch(() => ({}))
      if (!res.ok || j.success === false) throw new Error(j.error || 'Could not load walks')
      setDays(j.days || [])
      setSummary(j.summary || null)
      setWalks(j.walks || [])
      setTraces(j.traces || [])
      setTradingDay(j.trading_day || day || '')
      setOpenKey('')
    } catch (e) {
      setError(e.message || 'Could not load walks')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load(initialTradingDay || '') }, [load, initialTradingDay])

  React.useEffect(() => {
    if (!alertId) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl(`/api/alerts/${encodeURIComponent(alertId)}`))
        const json = await res.json().catch(() => ({}))
        if (!res.ok || json.success === false || cancelled) return
        const row = json.data || {}
        const details = typeof row.details === 'string'
          ? (() => { try { return JSON.parse(row.details) } catch { return {} } })()
          : (row.details || {})
        const egm = row.egm_house_no || details.EgmPK || details.egm_pk || details.EGM || initialEgm || ''
        const at = row.first_seen || details.SnapShotTime || details.SessionStart || ''
        const day = String(row.trading_day || details.trading_day || initialTradingDay || '').slice(0, 10)
        setAlertAnchor({ egm: String(egm || ''), at: at || '' })
        if (egm) setEgmQuery((current) => current || String(egm))
        if (day) {
          setTradingDay(day)
          load(day)
        }
      } catch {
        /* the tab still works without the alert highlight */
      }
    })()
    return () => { cancelled = true }
  }, [alertId, initialEgm, initialTradingDay, load])

  const journeySearch = { ticket: ticketQuery, egm: egmQuery, timeFrom, timeTo, amountMax }
  const shownHops = walks
    .filter((w) => matchesFilter(w, filter) && hopMatchesSearch(w, journeySearch))
    .slice()
    .sort((a, b) => Number(walkTouchesAlert(b, alertAnchor)) - Number(walkTouchesAlert(a, alertAnchor)))
  const shownTraces = traces
    .filter((t) => matchesTrace(t, filter) && passesWalkFilters(t, { ticketMin, amountMin, result }) && passesJourneySearch(t, journeySearch))
    .slice()
    .sort((a, b) => Number(traceTouchesAlert(b, alertAnchor)) - Number(traceTouchesAlert(a, alertAnchor)))

  const toggleTickets = (ids, on) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      for (const id of ids) {
        const key = String(id || '').trim()
        if (!key) continue
        if (on) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }

  const selectAlertTickets = () => {
    const ids = []
    for (const trace of traces) {
      if (traceTouchesAlert(trace, alertAnchor)) ids.push(...traceTicketIds(trace))
    }
    for (const walk of walks) {
      if (walkTouchesAlert(walk, alertAnchor) && walk.ticket_id) ids.push(walk.ticket_id)
    }
    toggleTickets(ids, true)
  }

  const attachToCase = async () => {
    const number = caseNumber.trim()
    if (!number) {
      toast.error('Enter the investigation number')
      return
    }
    if (!selectedIds.size) {
      toast.error('Tick the tickets to include')
      return
    }
    const items = []
    const seen = new Set()
    const push = (step) => {
      const id = String(step?.ticket_id || '').trim()
      if (!id || !selectedIds.has(id) || seen.has(id)) return
      seen.add(id)
      items.push({
        type: 'ticket',
        source_id: id,
        source_table: 'tito_vouchers',
        timestamp: step.to_time || step.from_time || null,
        payload: {
          ticket_id: id,
          from_place: step.from_place || null,
          to_place: step.to_place || null,
          amount: step.amount ?? null,
          from_time: step.from_time || null,
          to_time: step.to_time || null,
          alert_id: alertId || null,
        },
      })
    }
    for (const trace of traces) {
      for (const step of trace.steps || []) push(step)
      if (trace.ticket_id) push(trace)
    }
    for (const walk of walks) push(walk)
    if (!items.length) {
      toast.error('Those tickets are not on this trading day')
      return
    }
    setSavingCase(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(number)}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.success === false) throw new Error(json.error || 'Could not add the tickets')
      toast.success(`${items.length} ticket${items.length === 1 ? '' : 's'} added to ${number}`)
    } catch (e) {
      toast.error(e.message || 'Could not add the tickets')
    } finally {
      setSavingCase(false)
    }
  }
  const multi = traces.filter((t) => t.tickets >= 2)
  const chips = view === 'walks'
    ? [
      ['Walks', traces.length, null],
      ['2 or more tickets', multi.length, null],
      ['Longest', multi.reduce((n, t) => Math.max(n, t.tickets), 0), null],
      ['Started with cash in', traces.filter((t) => t.start === 'cash_in').length, null],
      ['Ended at a terminal', traces.filter((t) => t.ended === 'terminal').length, null],
    ]
    : [
      ['Ticket hops', summary?.walks, summary?.walk_amount],
      ['Under 15 min', summary?.under_15, summary?.under_15_amount],
      ['EGM to EGM', summary?.egm_egm, summary?.egm_egm_amount],
      ['EGM to terminal', summary?.egm_terminal, summary?.egm_terminal_amount],
      ['Still open', summary?.open, summary?.open_amount],
    ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Ticket journey</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            A walk starts at the latest ticket and follows it back. Each earlier ticket was inserted on the machine that printed the next one. When no ticket was inserted, that sit started with cash in. Search by EGM, ticket, time, or value, tick the tickets that belong on the investigation, and leave the rest off.
          </p>
        </div>
        <TitoUploadControl onImported={(j) => load((j.trading_days || [])[0] || tradingDay)} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-gray-600">
          Trading day
          <input
            type="date"
            className="input-field ml-2"
            value={tradingDay || ''}
            onChange={(e) => {
              setTradingDay(e.target.value)
              load(e.target.value)
            }}
          />
        </label>
        {days.length > 0 && (
          <span className="text-xs text-gray-500">
            {days.length} imported day{days.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {chips.map(([label, count, amount]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
              <div className="text-lg font-semibold text-slate-900">{count ?? 0}</div>
              <div className="text-xs text-slate-600">{amount == null ? '' : formatGamingMoney(amount || 0)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${view === 'walks' ? 'bg-psg-teal text-white' : 'bg-slate-100 text-slate-700'}`}
          onClick={() => { setView('walks'); setFilter('multi') }}
        >
          Walks
        </button>
        <button
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${view === 'tickets' ? 'bg-psg-teal text-white' : 'bg-slate-100 text-slate-700'}`}
          onClick={() => { setView('tickets'); setFilter('walks') }}
        >
          Each ticket
        </button>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {(view === 'walks' ? TRACE_FILTERS : FILTERS).map((f) => (
          <button
            key={f.id}
            type="button"
            className={`rounded-full px-3 py-1 text-xs ${filter === f.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {view === 'walks' && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-600">
            Tickets at least
            <input
              type="number"
              min="1"
              className="input-field ml-2 w-20"
              value={ticketMin}
              onChange={(e) => setTicketMin(e.target.value)}
            />
          </label>
          <label className="text-xs text-slate-600">
            End amount at least
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-field ml-2 w-28"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
            />
          </label>
          <label className="text-xs text-slate-600">
            Gain or loss
            <select
              className="input-field ml-2"
              value={result}
              onChange={(e) => setResult(e.target.value)}
            >
              <option value="all">All</option>
              <option value="gain">Gain</option>
              <option value="loss">Loss</option>
            </select>
          </label>
          <span className="pb-2 text-xs text-slate-500">{shownTraces.length} walks</span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-600">
          EGM
          <input className="input-field ml-2 w-28" value={egmQuery} onChange={(e) => setEgmQuery(e.target.value)} placeholder="Machine" />
        </label>
        <label className="text-xs text-slate-600">
          Ticket
          <input className="input-field ml-2 w-36" value={ticketQuery} onChange={(e) => setTicketQuery(e.target.value)} placeholder="Ticket number" />
        </label>
        <label className="text-xs text-slate-600">
          From
          <input type="datetime-local" className="input-field ml-2" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} />
        </label>
        <label className="text-xs text-slate-600">
          To
          <input type="datetime-local" className="input-field ml-2" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} />
        </label>
        <label className="text-xs text-slate-600">
          Value up to
          <input type="number" min="0" step="0.01" className="input-field ml-2 w-28" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} />
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <span className="pb-2 text-xs text-slate-600">{selectedIds.size} ticket{selectedIds.size === 1 ? '' : 's'} included</span>
        {alertAnchor ? (
          <button type="button" className="btn-secondary text-xs" onClick={selectAlertTickets}>Select alert tickets</button>
        ) : null}
        <button type="button" className="btn-secondary text-xs" onClick={() => setSelectedIds(new Set())}>Clear</button>
        <label className="text-xs text-slate-600">
          Investigation
          <input
            className="input-field ml-2 w-40"
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            placeholder="INV-2026-00001"
          />
        </label>
        <button type="button" className="btn-primary text-xs" disabled={savingCase} onClick={attachToCase}>
          {savingCase ? 'Adding…' : 'Add selected tickets'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Loading walks…</p>}
      {!loading && !error && (view === 'walks' ? shownTraces : shownHops).length === 0 && (
        <p className="text-sm text-gray-500">
          {walks.length === 0
            ? 'No TITO rows for this day. Upload the daily CSV.'
            : 'Nothing in this filter.'}
        </p>
      )}
      <div className="max-h-[70vh] overflow-auto rounded border border-slate-200 bg-white">
        {view === 'walks' && shownTraces.length > 0 && (
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-6 px-2 py-2" />
                <th className="px-2 py-2 text-right font-medium">Start</th>
                <th className="px-2 py-2 text-left font-medium">From</th>
                <th className="px-2 py-2 text-right font-medium">End</th>
                <th className="px-2 py-2 text-left font-medium">To</th>
                <th className="px-2 py-2 text-right font-medium">Gain / loss</th>
                <th className="px-2 py-2 text-right font-medium">Tickets</th>
              </tr>
            </thead>
            <tbody>
              {shownTraces.map((trace) => (
                <TraceRow
                  key={trace.ticket_id}
                  trace={trace}
                  highlighted={traceTouchesAlert(trace, alertAnchor)}
                  selectedIds={selectedIds}
                  onToggleTicket={toggleTickets}
                />
              ))}
            </tbody>
          </table>
        )}
        {view === 'tickets' && shownHops.map((walk) => {
          const key = `${walk.ticket_id}-${walk.from_time || ''}-${walk.to_time || ''}-${walk.kind}`
          return (
            <WalkRow
              key={key}
              walk={walk}
              open={openKey === key}
              highlighted={walkTouchesAlert(walk, alertAnchor)}
              selected={selectedIds.has(String(walk.ticket_id || ''))}
              onToggle={() => setOpenKey(openKey === key ? '' : key)}
              onToggleTicket={(id, on) => toggleTickets([id], on)}
            />
          )
        })}
      </div>
    </div>
  )
}
